<template>
    <div class="message">
        <div class="message__header">
            <div class="message__author">
                <div class="message__name">{{ message.name }}</div>
                <div v-if="message.notice" class="message__notice">{{ message.notice }}</div>
                <div v-if="message.is_private" class="message__badge message__badge--private">{{ $t('PRIVATE') }}</div>
            </div>
            <div class="message__meta">
                <div class="message__date">{{ formatDate(message.added_at) }}</div>
            </div>
        </div>

        <div class="message__content">
            <VMarkdown :content="message.message" />
        </div>

        <div v-if="canInteract" class="message__actions">
            <div v-if="!disallowReplies" @click="handleReply" class="message__action">{{ $t('REPLY') }}</div>
            <div @click="handleReport" class="message__action">{{ $t('REPORT') }}</div>
            <div v-if="showSetAsReadButton" @click="setMessageRead" class="message__action message__action--read"
                :class="{ 'message__action--loading': isSettingAsRead }">
                {{ isSettingAsRead ? $t('SETTING') : $t('SET_AS_READ') }}
            </div>
        </div>

        <div v-if="hasReplies" class="message__replies">
            <div v-if="!showReplies && replies.length > 0" class="message__show-replies">
                <button @click="toggleReplies" class="message__show-replies-btn">
                    {{ $t('SHOW_REPLIES', {
                        count: replies.length, reply: replies.length === 1 ? $t('REPLY') :
                            $t('REPLIES')
                    }) }}
                </button>
            </div>

            <transition name="replies-container">
                <div v-if="showReplies" class="message__replies-container">
                    <transition-group name="reply" tag="div" class="message__replies-list">
                        <div v-for="(reply, index) in visibleReplies" :key="reply.id" class="message__reply"
                            :style="{ animationDelay: index * 50 + 'ms' }">
                            <DiscussionMessage :message="reply" :replies="getNestedReplies(reply.id)" :topic="topic"
                                :nesting-level="nestingLevel + 1" :canCommentAsUser="canCommentAsUser"
                                :canCommentAsStaff="canCommentAsStaff" :disallowReplies="disallowReplies"
                                @reply="emitReply" @report="emitReport" @load-more-replies="handleLoadMoreReplies" />
                        </div>
                    </transition-group>

                    <div v-if="hasMoreReplies" class="message__load-more">
                        <button @click="loadMoreReplies" class="message__load-more-btn">
                            {{ $t('LOAD_MORE_REPLIES') }}
                        </button>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { format } from 'date-fns';
import Vue from 'vue';
import VMarkdown from './VMarkdown.vue';

export default {
  name: 'DiscussionMessage',
  components: {
    VMarkdown,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    replies: {
      type: Array,
      default: () => [],
    },
    topic: {
      type: String,
      required: true,
    },
    nestingLevel: {
      type: Number,
      default: 0,
    },
    canCommentAsUser: {
      type: Boolean,
      default: false,
    },
    canCommentAsStaff: {
      type: Boolean,
      default: false,
    },
    disallowReplies: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showReplies: this.nestingLevel < 2, // Auto-expand first two levels
      visibleCount: 5, // Initial number of visible replies
      loadedAllReplies: false,
      messageMarkedAsRead: false, // Track if this specific message has been marked as read
      isSettingAsRead: false, // Track if we're currently processing the set as read request
    };
  },
  computed: {
    hasReplies() {
      return this.replies && this.replies.length > 0;
    },
    visibleReplies() {
      return this.replies.slice(0, this.visibleCount);
    },
    hasMoreReplies() {
      return this.replies.length > this.visibleCount && !this.loadedAllReplies;
    },
    maxNestingLevel() {
      return 5; // Maximum nesting level to display
    },
    canHaveNestedReplies() {
      return this.nestingLevel < this.maxNestingLevel;
    },
    canInteract() {
      return (this.canCommentAsUser && !!this.$store.state.customerProfile?.email)
                || (this.canCommentAsStaff && !!this.$store.state.publisherProfile?.email);
    },
    showSetAsReadButton() {
      // If we've already marked this message as read locally, don't show the button
      if (this.message.is_private === 0) {
        return false;
      }
      if (this.messageMarkedAsRead) {
        return false;
      }

      const isCustomerMode = !!this.$store.state.customerProfile?.email;
      const isStaffMode = !!this.$store.state.publisherProfile?.email;

      if (isCustomerMode && (this.message.read_at_receiver === 0 && this.message.is_yours === 0 || this.message.read_at_sender === 0 && this.message.is_yours === 1)) {
        return true;
      }

      // Staff mode: show if message.read_at_staff==0
      if (isStaffMode && this.message.read_at_staff === 0) {
        return true;
      }

      return false;
    },
  },
  mounted() {
    // No initialization needed
  },
  methods: {
    getNestedReplies(messageId) {
      // Only return replies if we're below the max nesting level
      if (!this.canHaveNestedReplies) {
        return [];
      }

      // Find the root Discussion component
      let rootComponent = this;
      while (rootComponent.$parent && rootComponent.$parent.$options.name !== 'Discussion') {
        rootComponent = rootComponent.$parent;
      }

      // If we found the Discussion component, use its method
      if (rootComponent.$parent && rootComponent.$parent.$options.name === 'Discussion') {
        return rootComponent.$parent.getRepliesForMessage(messageId);
      }

      // Fallback to empty array if Discussion not found
      return [];
    },

    loadMoreReplies() {
      // Load more replies
      this.visibleCount += 5;

      // Check if we've loaded all replies
      if (this.visibleCount >= this.replies.length) {
        this.loadedAllReplies = true;
      }

      // Emit event to parent
      this.$emit('load-more-replies', this.message.id);
    },

    handleLoadMoreReplies(messageId) {
      // Pass the request up to the parent
      this.$emit('load-more-replies', messageId);
    },

    formatDate(timestamp) {
      if (!timestamp) return '';

      return format(new Date(timestamp * 1000), 'MMM d, yyyy HH:mm');
    },

    handleReply() {
      this.$emit('reply', this.message);
    },

    handleReport() {
      this.$emit('report', this.message);
    },

    emitReply(message) {
      this.$emit('reply', message);
    },

    emitReport(message) {
      this.$emit('report', message);
    },

    toggleReplies() {
      this.showReplies = !this.showReplies;
    },

    setMessageRead() {
      // Prevent multiple clicks
      if (this.isSettingAsRead) {
        return;
      }

      // Set loading state
      this.isSettingAsRead = true;

      // Find the root Discussion component
      let rootComponent = this;
      while (rootComponent.$parent && rootComponent.$parent.$options.name !== 'Discussion') {
        rootComponent = rootComponent.$parent;
      }

      // If we found the Discussion component, use its method
      if (rootComponent.$parent && rootComponent.$parent.$options.name === 'Discussion') {
        rootComponent.$parent.handleMessageRead(this.message.id)
          .then(() => {
            // Mark this message as read locally to hide the button
            this.messageMarkedAsRead = true;

            // Force update to ensure the UI reflects the change
            this.$nextTick(() => {
              this.$forceUpdate();
            });

            // Show success notification
            this.$notify({
              type: 'success',
              text: this.$t('MESSAGE_MARKED_AS_READ'),
            });
          })
          .catch((error) => {
            console.error('Failed to mark message as read:', error);
            this.$notify({
              type: 'error',
              text: this.$t('FAILED_TO_MARK_MESSAGE_AS_READ'),
            });
          })
          .finally(() => {
            this.isSettingAsRead = false;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message {
    padding: 8px;
    border-radius: 4px;
    background-color: var(--bg-secondary);
    margin-bottom: 4px;

    // All nested messages use theme-appropriate background color
    .message__replies & {
        background-color: var(--bg-tertiary, rgba(128, 128, 128, 0.1));
    }

    &__header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 6px;
    }

    &__author {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
    }

    &__name {
        font-weight: 500;
        font-size: 14px;
    }

    &__notice {
        color: var(--text-secondary);
        font-size: 12px;
    }

    &__badge {
        padding: 1px 4px;
        border-radius: 3px;
        font-size: 11px;

        &--private {
            background-color: #f8d7da;
            color: #721c24;
        }
    }

    &__meta {
        text-align: right;
        color: var(--text-secondary);
        font-size: 12px;
    }

    &__content {
        margin-bottom: 6px;
        color: var(--text-primary);
        word-break: break-word;
        font-size: 14px;

        ::v-deep p {
            margin-bottom: 0.5em;
        }

        ::v-deep img {
            max-width: 100%;
            border-radius: 4px;
            margin: 8px 0;
        }

        ::v-deep a {
            color: var(--primary);
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__actions {
        display: flex;
        gap: 10px;
        margin-bottom: 6px;
    }

    &__action {
        color: var(--primary);
        cursor: pointer;
        font-size: 12px;

        &:hover {
            text-decoration: underline;
        }

        &--read {
            color: #28a745;

            &:hover {
                color: #218838;
            }
        }

        &--loading {
            opacity: 0.7;
            cursor: not-allowed;

            &:hover {
                text-decoration: none;
            }
        }
    }

    &__replies {
        margin-top: 6px;
        margin-left: 12px;
        border-left: 2px solid var(--border-color);
        padding-left: 8px;
    }

    &__reply {
        margin-bottom: 4px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__show-replies {
        margin-bottom: 6px;
    }

    &__show-replies-btn,
    &__load-more-btn {
        background-color: transparent;
        border: none;
        color: var(--primary);
        cursor: pointer;
        font-size: 12px;
        padding: 3px 0;

        &:hover {
            text-decoration: underline;
        }
    }

    &__load-more {
        margin-top: 6px;
        text-align: center;
    }

    .replies-container-enter-active,
    .replies-container-leave-active {
        transition: all 0.3s ease-out;
        overflow: hidden;
        max-height: 2000px;
        /* Arbitrary large value */
    }

    .replies-container-enter,
    .replies-container-leave-to {
        max-height: 0;
        opacity: 0;
    }

    .reply-enter-active {
        animation: slide-in 0.3s ease-out forwards;
    }

    .reply-move {
        transition: transform 0.3s ease-out;
    }

    @keyframes slide-in {
        0% {
            opacity: 0;
            transform: translateX(-10px);
        }

        100% {
            opacity: 1;
            transform: translateX(0);
        }
    }

    .message__reply {
        animation: fade-in 0.3s ease-out forwards;
    }

    @keyframes fade-in {
        0% {
            opacity: 0;
        }

        100% {
            opacity: 1;
        }
    }
}
</style>
